import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../firebase';
import SEO from '../components/SEO';

const API_BASE = process.env.NODE_ENV === 'development' ? '' : (process.env.REACT_APP_API_URL || '');

export interface MediaItem {
  mediaId: string;
  name: string;
  kind: 'image' | 'document' | 'video' | 'audio';
  filename: string;
  originalName: string;
  sizeBytes: number;
  url: string;
  createdAt: number;
}

const KIND_ICONS: Record<string, string> = { image: '🖼️', document: '📄', video: '🎬', audio: '🎵' };
const ACCEPTED_EXTENSIONS = '.jpg,.jpeg,.png,.gif,.webp,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.mp4,.mov,.webm,.m4v,.mp3,.ogg,.wav,.m4a';

const S = {
  page: { background: 'var(--bg-page)', minHeight: '100vh', fontFamily: "'DM Sans', system-ui, sans-serif" } as React.CSSProperties,
  input: { width: '100%', padding: '9px 12px', background: 'var(--bg-page)', border: '1px solid var(--bg-element)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '13px', outline: 'none', boxSizing: 'border-box' as const },
  card: { background: 'var(--bg-card)', borderRadius: '14px', border: '1px solid var(--bg-element)', padding: '20px', marginBottom: '14px' } as React.CSSProperties,
  label: { display: 'block', fontSize: '11px', color: 'var(--text-muted)', fontWeight: '600', letterSpacing: '0.06em', textTransform: 'uppercase' as const, marginBottom: '6px' },
  btnPrimary: { padding: '8px 18px', background: '#5B4FE8', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' } as React.CSSProperties,
  btnDanger: { padding: '6px 12px', background: '#ef444420', color: '#ef4444', border: '1px solid #ef444440', borderRadius: '7px', cursor: 'pointer', fontSize: '12px' } as React.CSSProperties,
  btnGhost: { padding: '6px 14px', background: 'var(--bg-element)', color: 'var(--text-secondary)', border: '1px solid var(--bg-element)', borderRadius: '7px', cursor: 'pointer', fontSize: '12px' } as React.CSSProperties,
};

export function fmtBytes(n: number) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export async function fetchMedia(): Promise<{ items: MediaItem[]; usedBytes: number; limitBytes: number | null }> {
  const token = await auth.currentUser?.getIdToken();
  const r = await fetch(`${API_BASE}/api/media`, { headers: { Authorization: `Bearer ${token}` } });
  const data = await r.json();
  if (!r.ok || !data.success) throw new Error(data.error || 'Failed to load media');
  return { items: data.items || [], usedBytes: data.usage?.usedBytes || 0, limitBytes: data.usage?.limitBytes ?? null };
}

export async function uploadMedia(file: File, name?: string): Promise<MediaItem> {
  const token = await auth.currentUser?.getIdToken();
  const form = new FormData();
  form.append('file', file);
  if (name) form.append('name', name);
  const r = await fetch(`${API_BASE}/api/media/upload`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: form,
  });
  const data = await r.json();
  if (!r.ok || !data.success) throw new Error(data.error || 'Upload failed');
  return data as MediaItem;
}

export default function MediaLibrary() {
  const navigate = useNavigate();
  const [items, setItems] = useState<MediaItem[]>([]);
  const [usedBytes, setUsedBytes] = useState(0);
  const [limitBytes, setLimitBytes] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState('');
  const [filterKind, setFilterKind] = useState<'all' | 'image' | 'document' | 'video' | 'audio'>('all');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uid = auth.currentUser?.uid;

  const load = async () => {
    if (!uid) return;
    setLoading(true);
    try {
      const { items, usedBytes, limitBytes } = await fetchMedia();
      setItems(items);
      setUsedBytes(usedBytes);
      setLimitBytes(limitBytes);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [uid]);

  const openUpload = () => {
    setFile(null); setName(''); setUploadError('');
    setShowUpload(true);
  };

  const handleFileChosen = (f: File | null) => {
    setFile(f);
    if (f && !name) setName(f.name.replace(/\.[^.]+$/, ''));
  };

  const doUpload = async () => {
    if (!file) { setUploadError('Choose a file first.'); return; }
    setUploading(true);
    setUploadError('');
    try {
      await uploadMedia(file, name || file.name);
      setShowUpload(false);
      await load();
    } catch (e: any) {
      setUploadError(e.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const doDelete = async (id: string, name: string) => {
    if (!window.confirm(`Delete "${name}"? Any flow steps using this file's URL will break.`)) return;
    try {
      const token = await auth.currentUser?.getIdToken();
      const r = await fetch(`${API_BASE}/api/media/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await r.json();
      if (!r.ok || !data.success) throw new Error(data.error || 'Delete failed');
      setItems(list => list.filter(i => i.mediaId !== id));
      setUsedBytes(u => u - (items.find(i => i.mediaId === id)?.sizeBytes || 0));
    } catch (e: any) {
      alert(e.message || 'Delete failed');
    }
  };

  const copyUrl = (url: string) => {
    navigator.clipboard?.writeText(url).catch(() => {});
  };

  const filtered = filterKind === 'all' ? items : items.filter(i => i.kind === filterKind);
  const pct = limitBytes ? Math.min(100, (usedBytes / limitBytes) * 100) : 0;

  if (loading) return (
    <div style={{ ...S.page, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>
      Loading media library...
    </div>
  );

  return (
    <>
      <SEO title="Media Library" description="Upload and manage images, documents and videos for your chatbot flows" type="website" noindex />
      <div style={S.page}>
        <div style={{ maxWidth: '1080px', margin: '0 auto', padding: '32px 24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
            <div>
              <button onClick={() => navigate('/dashboard')} style={{ ...S.btnGhost, marginBottom: '12px' }}>← Dashboard</button>
              <h1 style={{ color: 'var(--text-primary)', fontSize: '24px', fontWeight: '700', margin: 0 }}>🖼️ Media Library</h1>
              <p style={{ color: 'var(--text-muted)', fontSize: '13px', margin: '4px 0 0' }}>Upload images, documents and videos here, then pick them straight from your flow builder — no external hosting needed.</p>
            </div>
            <button onClick={openUpload} style={S.btnPrimary}>+ Upload file</button>
          </div>

          <div style={{ ...S.card, display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: '220px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: 'var(--text-muted)', marginBottom: '6px' }}>
                <span>Storage used</span>
                <span>{fmtBytes(usedBytes)}{limitBytes != null ? ` / ${fmtBytes(limitBytes)}` : ' (unlimited)'}</span>
              </div>
              {limitBytes != null && (
                <div style={{ height: '8px', borderRadius: '4px', background: 'var(--bg-element)', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${pct}%`, background: pct > 90 ? '#ef4444' : '#5B4FE8', transition: 'width 0.3s' }} />
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              {(['all', 'image', 'document', 'video', 'audio'] as const).map(k => (
                <button key={k} onClick={() => setFilterKind(k)}
                  style={{ ...S.btnGhost, background: filterKind === k ? '#5B4FE8' : 'var(--bg-element)', color: filterKind === k ? 'white' : 'var(--text-secondary)' }}>
                  {k === 'all' ? 'All' : `${KIND_ICONS[k]} ${k[0].toUpperCase()}${k.slice(1)}`}
                </button>
              ))}
            </div>
          </div>

          {filtered.length === 0 ? (
            <div style={{ ...S.card, textAlign: 'center', padding: '48px 24px' }}>
              <div style={{ fontSize: '40px', marginBottom: '10px' }}>🖼️</div>
              <div style={{ color: 'var(--text-primary)', fontWeight: '600', marginBottom: '6px' }}>No media yet</div>
              <p style={{ color: 'var(--text-muted)', fontSize: '13px', marginBottom: '16px' }}>Upload your first image, document or video — perfect if you don't already have a website to host files on.</p>
              <button onClick={openUpload} style={S.btnPrimary}>+ Upload file</button>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '14px' }}>
              {filtered.map(item => (
                <div key={item.mediaId} style={{ ...S.card, marginBottom: 0, padding: '12px' }}>
                  <div style={{
                    height: '110px', borderRadius: '8px', background: 'var(--bg-page)', border: '1px solid var(--bg-element)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', marginBottom: '8px',
                  }}>
                    {item.kind === 'image' ? (
                      <img src={item.url} alt={item.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <span style={{ fontSize: '36px' }}>{KIND_ICONS[item.kind] || '📄'}</span>
                    )}
                  </div>
                  <div style={{ color: 'var(--text-primary)', fontWeight: '600', fontSize: '12px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: '2px' }}>{item.name}</div>
                  <div style={{ color: 'var(--text-faint)', fontSize: '10px', marginBottom: '8px' }}>{fmtBytes(item.sizeBytes)}</div>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button onClick={() => copyUrl(item.url)} style={{ ...S.btnGhost, flex: 1, fontSize: '11px' }}>🔗 Copy URL</button>
                    <button onClick={() => doDelete(item.mediaId, item.name)} style={S.btnDanger}>🗑</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {showUpload && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={() => !uploading && setShowUpload(false)}>
          <div style={{ background: 'var(--bg-card)', borderRadius: '16px', border: '1px solid var(--bg-element)', padding: '28px', maxWidth: '440px', width: '90%' }}
            onClick={e => e.stopPropagation()}>
            <h2 style={{ color: 'var(--text-primary)', fontSize: '16px', fontWeight: '700', margin: '0 0 16px' }}>Upload file</h2>

            <div style={{ marginBottom: '14px' }}>
              <label style={S.label}>File (image, document, video or audio — max 50MB)</label>
              <input ref={fileInputRef} type="file" accept={ACCEPTED_EXTENSIONS}
                onChange={e => handleFileChosen(e.target.files?.[0] || null)} style={S.input} />
            </div>
            <div style={{ marginBottom: '14px' }}>
              <label style={S.label}>Name</label>
              <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Product photo" style={S.input} />
            </div>
            {uploadError && <div style={{ color: '#ef4444', fontSize: '12px', marginBottom: '14px' }}>{uploadError}</div>}
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowUpload(false)} disabled={uploading} style={S.btnGhost}>Cancel</button>
              <button onClick={doUpload} disabled={uploading || !file} style={{ ...S.btnPrimary, opacity: uploading || !file ? 0.6 : 1 }}>
                {uploading ? 'Uploading…' : 'Upload'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
